import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, PageHeader } from '@/components/ui'
import { cn } from '@/lib/cn'
import { ArrowRight, Calculator, CheckCircle2, MapPin, Package, Truck } from 'lucide-react'

type ShippingType = 'domestic' | 'international'
type ServiceLevel = 'economy' | 'standard' | 'express'

type QuoteCarrier = {
  id: string
  name: string
  logo: string
  serviceDays: string
  coverage: string
  base: number
  perKg: number
  featured?: boolean
}

const DOMESTIC_CARRIERS: QuoteCarrier[] = [
  { id: 'interrapidisimo', name: 'Interrapidísimo', logo: '📦', serviceDays: '1-2 días', coverage: 'Cobertura nacional', base: 8200, perKg: 3100, featured: true },
  { id: 'servientrega', name: 'Servientrega', logo: '🚚', serviceDays: '1-3 días', coverage: 'Ciudades principales', base: 9000, perKg: 2900 },
  { id: 'coordinadora', name: 'Coordinadora', logo: '🏢', serviceDays: '2-3 días', coverage: 'Nacional', base: 7800, perKg: 2800 },
  { id: 'dhl', name: 'DHL', logo: '✈️', serviceDays: 'Express', coverage: 'Cobertura premium', base: 14500, perKg: 4200 },
]

const INTERNATIONAL_CARRIERS: QuoteCarrier[] = [
  { id: 'myshipping', name: 'MyShipping', logo: '🌍', serviceDays: '4-7 días', coverage: 'América y Europa', base: 28000, perKg: 8500, featured: true },
  { id: 'dhl', name: 'DHL Express', logo: '✈️', serviceDays: '2-5 días', coverage: 'Global', base: 36000, perKg: 9800 },
  { id: 'fedex', name: 'FedEx', logo: '📮', serviceDays: '3-6 días', coverage: 'Global', base: 33000, perKg: 9100 },
  { id: 'ups', name: 'UPS', logo: '📦', serviceDays: '3-6 días', coverage: 'Global', base: 32000, perKg: 9400 },
]

export const Route = createFileRoute('/store/$id/tarifas')({
  component: TariffQuotePage,
})

function TariffQuotePage() {
  const [shippingType, setShippingType] = useState<ShippingType>('domestic')
  const [origin, setOrigin] = useState('Bogotá')
  const [destination, setDestination] = useState('Medellín')
  const [weight, setWeight] = useState('2.5')
  const [declaredValue, setDeclaredValue] = useState('85000')
  const [serviceLevel, setServiceLevel] = useState<ServiceLevel>('standard')
  const [insurance, setInsurance] = useState(true)

  const carriers = shippingType === 'domestic' ? DOMESTIC_CARRIERS : INTERNATIONAL_CARRIERS

  const quotes = useMemo(() => {
    const weightValue = Number(weight) || 0
    const declared = Number(declaredValue) || 0
    const serviceMultiplier: Record<ServiceLevel, number> = {
      economy: 0.92,
      standard: 1,
      express: 1.22,
    }
    const insuranceFee = insurance ? Math.max(1800, declared * 0.008) : 0

    return carriers.map((carrier) => ({
      ...carrier,
      total: Math.round((carrier.base + weightValue * carrier.perKg) * serviceMultiplier[serviceLevel] + insuranceFee),
    }))
  }, [carriers, weight, declaredValue, serviceLevel, insurance])

  const [firstQuote, ...otherQuotes] = quotes
  const bestQuote = firstQuote ? otherQuotes.reduce((lowest, current) => (current.total < lowest.total ? current : lowest), firstQuote) : null

  return (
    <>
      <PageHeader title="Tarifas y cotizador" description="Mockup UI para estimar envíos nacionales e internacionales" />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Cotizaciones" value={quotes.length} icon={Calculator} color="text-cyan-700 dark:text-cyan-400" />
          <StatCard label="Cobertura" value={shippingType === 'domestic' ? 100 : 42} icon={Truck} color="text-emerald-700 dark:text-emerald-400" suffix="%" />
          <StatCard label="Peso" value={Number(weight) || 0} icon={Package} color="text-[hsl(var(--primary))]" suffix="kg" />
          <StatCard label="Valor" value={Number(declaredValue) || 0} icon={CheckCircle2} color="text-blue-700 dark:text-blue-400" prefix="$" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <Card className="xl:col-span-2 border-cyan-200 dark:border-cyan-900/30 bg-background/70">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-xl">Cotizador rápido</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Mockup visual para comparar operadores y rangos de precio.</p>
                </div>
                <div className="flex rounded-lg border border-border bg-[hsl(var(--surface))] p-1">
                  {(['domestic', 'international'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setShippingType(type)}
                      className={cn(
                        'px-3 py-1.5 text-xs rounded-md transition',
                        shippingType === type ? 'bg-cyan-500 text-black font-medium' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {type === 'domestic' ? 'Nacional' : 'Internacional'}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={shippingType === 'domestic' ? 'Ciudad origen' : 'País origen'} value={origin} onChange={setOrigin} icon={<MapPin className="size-4" />} />
              <Field label={shippingType === 'domestic' ? 'Ciudad destino' : 'País destino'} value={destination} onChange={setDestination} icon={<MapPin className="size-4" />} />
              <Field label="Peso (kg)" value={weight} onChange={setWeight} icon={<Package className="size-4" />} inputMode="decimal" />
              <Field label="Valor declarado" value={declaredValue} onChange={setDeclaredValue} icon={<CheckCircle2 className="size-4" />} inputMode="numeric" />

              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                {(['economy', 'standard', 'express'] as const).map((level) => (
                  <button
                    key={level}
                    onClick={() => setServiceLevel(level)}
                    className={cn(
                      'rounded-xl border p-4 text-left transition',
                      serviceLevel === level ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30' : 'border-border bg-[hsl(var(--surface))] hover:border-border',
                    )}
                  >
                    <p className="text-sm font-medium capitalize">{level === 'economy' ? 'Económico' : level === 'standard' ? 'Estándar' : 'Express'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {level === 'economy' ? 'Menor costo, más tiempo' : level === 'standard' ? 'Balance entre precio y velocidad' : 'Entrega prioritaria'}
                    </p>
                  </button>
                ))}
              </div>

              <label className="md:col-span-2 flex items-center justify-between rounded-xl border border-border bg-[hsl(var(--surface))] px-4 py-3 text-sm">
                <span>Agregar seguro al envío</span>
                <input type="checkbox" checked={insurance} onChange={(e) => setInsurance(e.target.checked)} className="size-4 accent-cyan-500" />
              </label>
            </CardContent>
          </Card>

          <Card className="border-cyan-200 dark:border-cyan-900/30 bg-gradient-to-br from-cyan-100 dark:from-cyan-950/30 to-background">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowRight className="size-4 text-cyan-700 dark:text-cyan-400" /> Resultado estimado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-2xl border border-cyan-200 dark:border-cyan-800/50 bg-background/60 p-4">
                <p className="text-xs text-muted-foreground">Mejor opción</p>
                <p className="mt-1 text-lg font-semibold">{bestQuote?.name ?? 'Sin tarifa'}</p>
                <p className="text-xs text-muted-foreground mt-1">{bestQuote ? `${bestQuote.serviceDays} • ${bestQuote.coverage}` : 'Agrega datos para ver resultados'}</p>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Tarifa estimada</p>
                    <p className="text-3xl font-bold text-cyan-700 dark:text-cyan-300">{bestQuote ? `$${bestQuote.total.toLocaleString('es-CO')}` : '--'}</p>
                  </div>
                  <Badge variant="success">Recomendado</Badge>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-[hsl(var(--surface))] p-4 text-sm space-y-2">
                <p className="text-foreground font-medium">Resumen del cálculo</p>
                <p className="text-muted-foreground">Origen: {origin}</p>
                <p className="text-muted-foreground">Destino: {destination}</p>
                <p className="text-muted-foreground">Tipo: {shippingType === 'domestic' ? 'Nacional' : 'Internacional'}</p>
                <p className="text-muted-foreground">Seguro: {insurance ? 'Sí' : 'No'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Comparador de tarifas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quotes.map((quote) => (
                <div
                  key={quote.id}
                  className={cn(
                    'flex items-center justify-between rounded-xl border px-4 py-3',
                    quote.featured ? 'border-cyan-200 dark:border-cyan-700 bg-cyan-50 dark:bg-cyan-950/20' : 'border-border bg-[hsl(var(--surface))]',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{quote.logo}</div>
                    <div>
                      <p className="text-sm font-medium">{quote.name}</p>
                      <p className="text-xs text-muted-foreground">{quote.serviceDays} • {quote.coverage}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">${quote.total.toLocaleString('es-CO')}</p>
                    <p className="text-xs text-muted-foreground">Base ${quote.base.toLocaleString('es-CO')}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Reglas visuales</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <InfoTile title="Peso volumétrico" description="Se muestra el mayor entre peso real y volumétrico." />
              <InfoTile title="Cobertura" description="El operador cambia según ciudad, país y servicio." />
              <InfoTile title="Seguro" description="Se suma un recargo sobre el valor declarado." />
              <InfoTile title="Tiempo estimado" description="Se ajusta por nivel de servicio y operador." />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Rangos de referencia</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <RangeCard title="Económico" description="Ideal para envíos sin urgencia." amount="Desde $7.800" tone="text-emerald-700 dark:text-emerald-400" />
            <RangeCard title="Estándar" description="Balance entre costo y velocidad." amount="Desde $8.500" tone="text-cyan-700 dark:text-cyan-400" />
            <RangeCard title="Express" description="Prioridad de entrega y seguimiento." amount="Desde $14.500" tone="text-[hsl(var(--primary))]" />
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function Field({
  label,
  value,
  onChange,
  icon,
  inputMode,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  icon: React.ReactNode
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}) {
  return (
    <label className="space-y-2">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{icon}</span>
        <Input value={value} onChange={(e) => onChange(e.target.value)} inputMode={inputMode} className="pl-10" />
      </div>
    </label>
  )
}

function StatCard({
  label,
  value,
  icon: Icon,
  color,
  prefix = '',
  suffix = '',
}: {
  label: string
  value: number
  icon: React.ComponentType<{ className?: string }>
  color: string
  prefix?: string
  suffix?: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-4">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-1">
              {prefix}{typeof value === 'number' ? value.toLocaleString('es-CO') : value}{suffix}
            </p>
          </div>
          <Icon className={cn('size-6', color)} />
        </div>
      </CardContent>
    </Card>
  )
}

function InfoTile({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--surface))] p-4">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  )
}

function RangeCard({ title, description, amount, tone }: { title: string; description: string; amount: string; tone: string }) {
  return (
    <div className="rounded-xl border border-border bg-[hsl(var(--surface))] p-4">
      <p className={cn('text-lg font-semibold', tone)}>{title}</p>
      <p className="text-sm text-foreground mt-1">{description}</p>
      <p className="text-sm text-muted-foreground mt-3">{amount}</p>
    </div>
  )
}
